import { StoreCard } from '../../merchant/components/StoreCard';
import { Heading2, Link } from '@shopmuchmore/design-system';
import { ChevronRight } from 'lucide-react';
import { useLocationStore } from '../../local/store/location.store';
import { useNearbyMerchantsQuery } from '../../merchant/merchant.services';
import { Skeleton } from '@shopmuchmore/design-system';

export function MerchantsNearbySection() {
  const { latitude, longitude } = useLocationStore();
  
  const { data, isLoading } = useNearbyMerchantsQuery(
    { lat: latitude as number, lng: longitude as number },
    { enabled: !!latitude && !!longitude }
  );

  if (!latitude || !longitude) {
    return null;
  }

  const merchants = data?.results || [];
  const totalCount = data?.total_merchant_count ?? data?.count ?? merchants.length;
  const showViewAll = !isLoading && totalCount > 1;

  if (!isLoading && merchants.length === 0) {
    return null;
  }

  return (
    <section className="w-full max-w-7xl mx-auto pb-8 mb-8">
      <div className="flex items-center justify-between mb-5 sm:mb-8">
        <div>
          <Heading2 className="text-foreground font-semibold mb-0 text-xl sm:text-2xl">
            Stores Nearby
          </Heading2>
          <p className="text-xs text-muted-foreground mt-0.5 sm:hidden">
            Local sellers delivering in your area
          </p>
        </div>
        {showViewAll && (
          <Link
            size="sm"
            variant="default"
            trailingIcon={<ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />}
            href="/nearby-merchants"
            className="text-xs sm:text-sm font-semibold shrink-0"
          >
            View All
          </Link>
        )}
      </div>
      
      {/* Responsive Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-2">
              <Skeleton className="h-40 w-full rounded-3xl" />
              <Skeleton className="h-6 w-3/4 mt-4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))
        ) : (
          merchants.slice(0, 6).map((merchant) => (
            <StoreCard 
              key={merchant.merchant_slug} 
              id={merchant.merchant_slug}
              name={merchant.shop_name}
              bannerUrl={merchant.banner || undefined}
              logoUrl={merchant.logo || undefined}
              city={merchant.city}
              stateOrRegion={merchant.state_or_region}
              rating={merchant.merchant_rating ?? merchant.rating} 
              reviewCount={merchant.merchant_review_count ?? merchant.review_count}
              deliveryTime={merchant.estimated_delivery_time || "24 hours"}
              distanceInMiles={merchant.distance_in_miles}
              openingTime={merchant.opening_time}
              closingTime={merchant.closing_time}
              openOnSunday={merchant.is_open_sunday ?? merchant.open_on_sunday ?? merchant.is_open_on_sunday}
            />
          ))
        )}
      </div>
    </section>
  );
}
